// usePatientSearch.js
import { useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

import { iaRa } from 'lib/helpers/utility';
import { usePatients } from 'lib/hooks/queries/patients/usePatients';

export const usePatientSearch = ({ enabled }) => {
  const [filters, setFilters] = useState({ searchTerm: '', page: 1, limit: 25, search: '' });
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [allPatients, setAllPatients] = useState([]);
  const divRef = useRef();

  const { searchTerm, page, limit } = filters;

  const { data, isFetching } = usePatients({
    params: { searchTerm, page, limit },
    dependencies: [searchTerm, page, limit],
    options: {
      enabled
    }
  });

  const count = data?.count ? Number(data.count) : 0;

  const patients = data?.patients;

  useEffect(() => {
    if (!patients) return;

    if (filters.page === 1) {
      setAllPatients(iaRa(patients));
    } else {
      setAllPatients((prev) => {
        const safePrev = iaRa(prev);
        const existingIds = new Set(safePrev.map((p) => p.id));
        const newPatients = iaRa(patients).filter((p) => !existingIds.has(p.id));
        return [...safePrev, ...newPatients];
      });
    }
  }, [patients, filters.page]);

  const debouncedUpdate = useMemo(
    () =>
      debounce((search) => {
        setFilters((prev) => ({
          ...prev,
          searchTerm: search,
          page: 1
        }));
      }, 500),
    []
  );

  useEffect(() => {
    debouncedUpdate(filters.search);

    return () => {
      debouncedUpdate.cancel();
    };
  }, [filters.search]);

  const handleScroll = () => {
    const div = divRef.current;
    if (div && !isFetching) {
      const hasMore = count > allPatients.length;
      if (Math.ceil(div.scrollTop) + div.clientHeight >= div.scrollHeight && hasMore) {
        setFilters((prev) => ({ ...prev, page: prev.page + 1 }));
      }
    }
  };

  return {
    filters,
    selectedPatient,
    allPatients,
    isFetching,
    divRef,
    setFilters,
    setSelectedPatient,
    handleScroll
  };
};
